import { cn } from "@devhop/ui";
import type {
  EventFxRatesDTO,
  EventProfitLossDTO,
  EventProfitLossSnapshotDTO,
} from "../../domain/contracts";
import { formatPerformanceMoney } from "../ui/PerformanceReportsNav";

export type ProfitLossView = "realized" | "projected";
export type ProfitDisplayMode = "native" | "converted";

export function pickProfitSnapshot(
  profitLoss: EventProfitLossDTO,
  view: ProfitLossView,
  displayMode: ProfitDisplayMode,
): EventProfitLossSnapshotDTO {
  if (
    displayMode === "converted" &&
    profitLoss.converted
  ) {
    return view === "realized"
      ? profitLoss.converted.realized
      : profitLoss.converted.projected;
  }
  return view === "realized" ? profitLoss.realized : profitLoss.projected;
}

export function defaultProfitDisplayMode(
  profitLoss: EventProfitLossDTO,
  view: ProfitLossView,
): ProfitDisplayMode {
  if (!profitLoss.hasForeignRevenue) return "native";
  const converted = profitLoss.converted;
  if (!converted) return "native";
  const revenueView =
    view === "realized"
      ? converted.revenueCollected
      : converted.revenueProjected;
  if (revenueView.conversionComplete) return "converted";
  const native =
    view === "realized" ? profitLoss.realized : profitLoss.projected;
  if (native.profit != null) return "native";
  return "converted";
}

export function formatFxRateSummary(rates: EventFxRatesDTO): string {
  const parts: string[] = [];
  if (rates.fxUsdToLak != null) {
    parts.push(
      `1 USD = ${formatPerformanceMoney(rates.fxUsdToLak, "LAK")}`,
    );
  }
  if (rates.fxThbToLak != null) {
    parts.push(
      `1 THB = ${formatPerformanceMoney(rates.fxThbToLak, "LAK")}`,
    );
  }
  return parts.join(" · ");
}

export function displayModeLabel(mode: ProfitDisplayMode): string {
  return mode === "converted" ? "LAK ເທົ່າຄ່າ" : "LAK ຕົງ";
}

export function profitTone(
  profit: number | null,
): "profit" | "loss" | "neutral" | "unknown" {
  if (profit == null) return "unknown";
  if (profit > 0) return "profit";
  if (profit < 0) return "loss";
  return "neutral";
}

export function formatSignedProfit(profit: number | null): string {
  if (profit == null) return "—";
  const prefix = profit > 0 ? "+" : profit < 0 ? "−" : "";
  const amount = formatPerformanceMoney(Math.abs(profit), "LAK");
  return profit === 0 ? amount : `${prefix}${amount}`;
}

export function profitHeadline(profit: number | null): string {
  if (profit == null) return "ຍັງຄິດບໍ່ໄດ້";
  if (profit > 0) return "ກຳໄລ";
  if (profit < 0) return "ຂາດທຶນ";
  return "ຄຸມທຸນ";
}

export function profitToneClass(tone: ReturnType<typeof profitTone>): string {
  switch (tone) {
    case "profit":
      return "text-emerald-600 dark:text-emerald-400";
    case "loss":
      return "text-destructive";
    case "neutral":
      return "text-foreground";
    default:
      return "text-muted-foreground";
  }
}

export function profitHeroBgClass(tone: ReturnType<typeof profitTone>): string {
  switch (tone) {
    case "profit":
      return "border-emerald-500/30 bg-emerald-500/5";
    case "loss":
      return "border-destructive/30 bg-destructive/5";
    case "neutral":
      return "border-border bg-muted/20";
    default:
      return "border-border bg-muted/10";
  }
}

export function viewLabel(view: ProfitLossView): string {
  return view === "realized" ? "ເງິນຈິງ" : "ຄາດການ";
}

export function viewHint(view: ProfitLossView): string {
  return view === "realized"
    ? "ເງິນເກັບແລ້ວ − ຕົ້ນທຸນທີ່ຈ່າຍແລ້ວ"
    : "ລາຍຮັບຄາດ (INV/QT) − ຕົ້ນທຸນທີ່ commit";
}
